import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { AppThunk } from 'store'
import { ApiAction, createApiAction } from 'api'
import { getRequirementDetails } from 'core/completion/api/completionService'
import { userLoggedOut } from 'core/session/slices/session'
import {
  getOnboardAssessmentDetails,
  markOnboardAssessment,
  uploadAssessmentDocument,
  removeAssessmentDocument,
} from 'modules/activityDetails/OnboardAssessmentDetails/api/onboardAssessmentDetailsService'
import {
  OnboardAssessmentDetailsState,
  ApiOnboardAssessmentDetailsResponse,
  ApiOnboardAssessmentMarkResponse,
  ApiOnboardAssessmentUploadDocumentResponse,
  ApiOnboardAssessmentRemoveDocumentResponse,
} from './types'

const initialState: OnboardAssessmentDetailsState = {
  onboardAssessmentDetailsData: {},
  onboardAssessmentMarked: {},
  onboardAssessmentDocument: {},
  onboardAssessmentDocumentRemoved: {},
}

const onboardAssessmentDetailsSlice = createSlice({
  name: 'onboardAssessmentDetails',
  initialState,
  reducers: {
    onboardAssessmentDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiOnboardAssessmentDetailsResponse>>
    ) {
      state.onboardAssessmentDetailsData = action.payload
    },
    clearOnboardAssessmentDetails(state) {
      state.onboardAssessmentDetailsData = initialState.onboardAssessmentDetailsData
    },
    onboardAssessmentMarkProcessed(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiOnboardAssessmentMarkResponse>>
    ) {
      state.onboardAssessmentMarked = action.payload
    },
    onboardAssessmentDocumentUploaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiOnboardAssessmentUploadDocumentResponse>>
    ) {
      state.onboardAssessmentDocument = action.payload
    },
    onboardAssessmentDocumentRemoveProcessed(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiOnboardAssessmentRemoveDocumentResponse>>
    ) {
      state.onboardAssessmentDocumentRemoved = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  onboardAssessmentDetailsLoaded,
  clearOnboardAssessmentDetails,
  onboardAssessmentMarkProcessed,
  onboardAssessmentDocumentUploaded,
  onboardAssessmentDocumentRemoveProcessed,
} = onboardAssessmentDetailsSlice.actions

export { clearOnboardAssessmentDetails }
export default onboardAssessmentDetailsSlice.reducer

export const loadOnboardAssessmentDetailsData = (competenceId: Guid, assessmentId: Guid) => {
  return createApiAction({
    apiCalls: {
      completionDetails: () => getRequirementDetails(competenceId),
      onboardAssessmentDetails: () => getOnboardAssessmentDetails(assessmentId),
    },
    payloadAction: onboardAssessmentDetailsLoaded,
  })
}

export const reloadOnboardAssessmentDetails = (competenceId: Guid, assessmentId: Guid) => {
  return createApiAction({
    apiCalls: {
      completionDetails: () => getRequirementDetails(competenceId),
      onboardAssessmentDetails: () => getOnboardAssessmentDetails(assessmentId),
    },
    payloadAction: onboardAssessmentDetailsLoaded,
    shouldShowGlobalLoading: true,
    getResultToKeep: ({ onboardAssessmentDetails }) =>
      onboardAssessmentDetails.onboardAssessmentDetailsData.result,
  })
}

const handleReloadOnboardAssessmentDetails =
  (response: ApiMiddlewarePayload, payloadAction: ApiAction['payloadAction']): AppThunk =>
  async (dispatch, getState) => {
    dispatch(payloadAction(response))

    if (!response.error) {
      const { onboardAssessmentDetails } = getState()
      const { completionDetails, onboardAssessmentDetails: details } =
        onboardAssessmentDetails.onboardAssessmentDetailsData?.result || {}
      const { id: competenceId } = completionDetails || {}
      const { id: assessmentId } = details || {}
      competenceId &&
        assessmentId &&
        dispatch(reloadOnboardAssessmentDetails(competenceId, assessmentId))
    }
  }

export const markOnboardAssessmentDetails = (assessmentId: Guid, IsReadyToBeAssessed: boolean) => {
  return createApiAction({
    apiCalls: {
      onboardAssessmentMark: () => markOnboardAssessment(assessmentId, IsReadyToBeAssessed),
    },
    payloadAction: onboardAssessmentMarkProcessed,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
    nextAction: {
      action: handleReloadOnboardAssessmentDetails,
      params: onboardAssessmentMarkProcessed,
    },
  })
}

export const uploadOnboardAssessmentDocument = (activityId: Guid, files: File[]) => {
  return createApiAction({
    apiCalls: {
      assessmentDocument: () => uploadAssessmentDocument(activityId, files),
    },
    payloadAction: onboardAssessmentDocumentUploaded,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
    nextAction: {
      action: handleReloadOnboardAssessmentDetails,
      params: onboardAssessmentDocumentUploaded,
    },
  })
}

export const deleteOnboardAssessmentDocument = (documentId: Guid) => {
  return createApiAction({
    apiCalls: {
      assessmentDocumentRemoved: () => removeAssessmentDocument(documentId),
    },
    payloadAction: onboardAssessmentDocumentRemoveProcessed,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
    nextAction: {
      action: handleReloadOnboardAssessmentDetails,
      params: onboardAssessmentDocumentRemoveProcessed,
    },
  })
}
